import {
  Image,
  Circle,
  Particle,
  Triangle,
  Rectangle,
  type Shape,
  type ParticleType
} from './particle';

import CSS from './Particles.module.css';
import { useRef, useEffect } from 'react';
import { DELTA_FRAME } from '@CS/utils/Number';

type ParticlesProps = {
  particle?: Particle;
  autoplay?: boolean;
  duration?: number;

  amount?: number;
  height?: number;
  width?: number;

  delay?: number;
  once?: boolean;
  play?: boolean;
  stop?: boolean;
};

export const Particles = ({
  particle = Particle.Rectangle,
  duration = Infinity,
  autoplay = false,

  height = 100.0,
  amount = 50.0,
  width = 150.0,

  once = false,
  play = false,
  stop = false,
  delay = 0.0
}: ParticlesProps) => {
  let canvasWidth = 0;
  let canvasHeight = 0;

  const raf = useRef(0.0);
  const playing = useRef(false);

  const actualWidth = width * 0.01;
  const actualHeight = height * 0.01;

  const dropOnce = particle !== Particle.Triangle;
  const canvas = useRef<HTMLCanvasElement | null>(null);
  const context = useRef<CanvasRenderingContext2D | null>(null);

  isFinite(duration) &&
    setTimeout(() => playing.current && stopParticles(), duration);

  const particles = useRef<Shape[]>(Array.from({ length: amount }));

  const createParticles = () => {
    let ParticleShape: ParticleType = Rectangle;

    switch (particle) {
      case Particle.Image:
        ParticleShape = Image;
        break;

      case Particle.Circle:
        ParticleShape = Circle;
        break;

      case Particle.Triangle:
        ParticleShape = Triangle;
        break;
    }

    ParticleShape.DropOnce = once;

    for (let c = particles.current.length; c--; ) {
      particles.current[c] = new ParticleShape(
        context.current as CanvasRenderingContext2D
      );
    }

    once &&
      !dropOnce &&
      console.error(
        '😱 One-time drop is not implemented in this type of particles yet.',
        '\n',
        'Please, update the particle class or remove "once" propriety and use a fixed duration.'
      );
  };

  const onScreenResize = () => {
    canvasWidth = window.innerWidth * actualWidth;
    canvasHeight = window.innerHeight * actualHeight;

    for (let c = particles.current.length; c--; ) {
      particles.current[c].setSize(canvasWidth, canvasHeight);
    }

    if (canvas.current) {
      canvas.current.width = canvasWidth;
      canvas.current.height = canvasHeight;
    }
  };

  const startParticles = () => {
    particles.current.forEach(c => c.start());
    requestAnimationFrame(animate);
    playing.current = true;
  };

  const stopParticles = () => {
    particles.current.forEach(c => c.stop());
    playing.current = false;

    setTimeout(() => {
      cancelAnimationFrame(raf.current);
      canvasWidth = window.innerWidth * actualWidth;
      canvasHeight = window.innerHeight * actualHeight;
      context.current?.clearRect(0, 0, canvasWidth, canvasHeight);
    }, DELTA_FRAME * 1e5);
  };

  const animate = () => {
    context.current?.clearRect(0, 0, canvasWidth, canvasHeight);
    raf.current = requestAnimationFrame(animate);
    particles.current.forEach(c => c.draw());
  };

  useEffect(() => {
    if (playing.current) return;

    if (play) startParticles();
    else if (autoplay) setTimeout(startParticles, delay);
  }, [play, autoplay]);

  useEffect(() => {
    if (!playing.current) return;
    stop && stopParticles();
  }, [stop]);

  useEffect(() => {
    window.addEventListener('resize', onScreenResize, false);

    context.current = canvas.current?.getContext(
      '2d'
    ) as CanvasRenderingContext2D;

    createParticles();
    onScreenResize();

    return () => window.removeEventListener('resize', onScreenResize, false);
  }, []);

  return (
    <canvas
      style={{ width: `${width}%`, height: `${height}%` }}
      className={CSS.particles}
      ref={canvas}
    />
  );
};
